import type { ReactNode } from "react";
import { StyleSheet, Text } from "react-native";

import { fonts, useThemedStyles, type ThemeColors } from "~/styles/theme";
import type { TextSize, TextWeight } from "./types";

export function AppText({
  children,
  size = "sm",
  weight,
  tone = "default",
  selectable,
  numberOfLines,
}: {
  children: ReactNode;
  size?: TextSize;
  weight?: TextWeight;
  tone?: "default" | "muted" | "danger" | "inverse" | "link" | "accent";
  selectable?: boolean;
  numberOfLines?: number;
}) {
  const { sizeStyles, weightStyles, toneStyles } = useThemedStyles(makeStyles);
  return (
    <Text
      selectable={selectable}
      numberOfLines={numberOfLines}
      style={[
        sizeStyles[size],
        weight ? weightStyles[weight] : null,
        toneStyles[tone],
      ]}
    >
      {children}
    </Text>
  );
}

const makeStyles = (colors: ThemeColors) => {
  const stylesheet = StyleSheet.create({
    xs: { fontFamily: fonts.regular, fontSize: 12, lineHeight: 16 },
    sm: { fontFamily: fonts.regular, fontSize: 14, lineHeight: 20 },
    base: { fontFamily: fonts.regular, fontSize: 16, lineHeight: 23 },
    lg: { fontFamily: fonts.semibold, fontSize: 19, lineHeight: 25 },
    xl: { fontFamily: fonts.bold, fontSize: 25, lineHeight: 30 },
    medium: { fontFamily: fonts.medium },
    semibold: { fontFamily: fonts.semibold },
    bold: { fontFamily: fonts.bold },
    toneDefault: { color: colors.foreground },
    toneMuted: { color: colors.mutedForeground },
    toneDanger: { color: colors.danger },
    toneInverse: { color: colors.onSteel },
    toneLink: { color: colors.primary },
    toneAccent: { color: colors.primary },
  });
  return {
    sizeStyles: {
      xs: stylesheet.xs,
      sm: stylesheet.sm,
      base: stylesheet.base,
      lg: stylesheet.lg,
      xl: stylesheet.xl,
    },
    weightStyles: {
      medium: stylesheet.medium,
      semibold: stylesheet.semibold,
      bold: stylesheet.bold,
    },
    toneStyles: {
      default: stylesheet.toneDefault,
      muted: stylesheet.toneMuted,
      danger: stylesheet.toneDanger,
      inverse: stylesheet.toneInverse,
      link: stylesheet.toneLink,
      accent: stylesheet.toneAccent,
    },
  };
};
